import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { of, throwError } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { PhxConstants, WindowRefService } from '@common';
import { MODULE_ROUTES } from '@common/constants/module-routes.const';
import { BaseComponentOnDestroy } from '@common/epics/base-component-on-destroy';
import { AuthService } from '@common/services/auth.service';
import { BroadcastChannelService } from '@common/services/broadcast-channel.service';

import { AppInitService } from '../../app-init.service';

import HTTPResponseStatus = PhxConstants.HTTPResponseStatus;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LoginComponent extends BaseComponentOnDestroy implements OnInit, AfterViewInit {
  form: UntypedFormGroup;
  isLoginFailed: boolean;
  userIpRestrictionError: string = null;
  askPassword = false;
  isValidatingEmail = false;
  isLoggingIn = false;
  readonly loginErrorMessage = 'The email or password you entered is incorrect. Please try again.';
  readonly redirectToPortalMessage = 'We\'ve upgraded your experience and moved sign-in to a new portal. You will be redirected shortly.';
  readonly redirectEmailSentMessage = 'We\'ve upgraded your experience and moved sign-in to a new portal. Please check your email for more information.';
  errorMessage: string;
  redirectMessage: string;
  @ViewChild('emailInputField') emailInputField: ElementRef<HTMLInputElement>;
  @ViewChild('passwordInputField') passwordInputField: ElementRef<HTMLInputElement>;

  allowMigratedUser = false;
  instanceId?: number;
  private broadcastChannelService = inject(BroadcastChannelService);

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private appInitService: AppInitService,
    private winRef: WindowRefService
  ) {
    super();
  }

  ngOnInit() {
    this.form = this.fb.group({
      email: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.email
      ]],
      password: ['', [
        Validators.required,
        Validators.minLength(6)
      ]]
    });

    this.route.queryParams.subscribe(params => {
      this.allowMigratedUser = params.allowMigratedUser;
      this.instanceId = params.iid ? Number(params.iid) : undefined;
    });
  }

  ngAfterViewInit(): void {
    if (!this.askPassword) {
      this.focusOnEmailField();
    }
  }

  checkUserEmail() {
    const email: string = (this.form.get('email').value as string).trim();
    if (email) {
      this.isValidatingEmail = true;
      this.form.get('email').disable();
      this.authService
        .validateUserEmail(email, this.allowMigratedUser)
        .pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse && err.status === 404) {
              return of(null);
            }
            return throwError(() => err);
          }),
          takeUntil(this.isDestroyed$)
        )
        .subscribe({
          next: () => {
            this.isValidatingEmail = false;
            this.askPassword = true;
            this.isLoginFailed = false;
            this.userIpRestrictionError = null;
            this.cdr.detectChanges();
            this.focusOnPasswordField();
          },
          error: err => {
            this.handleLoginError(err);
          }
        });
    }
  }

  login() {
    const email = (this.form.get('email').value as string).trim();
    const password = (this.form.get('password').value as string).trim();
    if (email && password) {
      this.isLoggingIn = true;
      this.authService.clearCookiesAndCachedItems();
      this.isLoginFailed = false;
      this.userIpRestrictionError = null;

      this.authService.login(email, password, undefined, this.instanceId)
        .pipe(takeUntil(this.isDestroyed$))
        .subscribe({
          next: () => {
            this.appInitService.initApp().then(() => {
              this.authService.redirectAfterLogin();
              this.broadcastChannelService.sendMessage(
                this.broadcastChannelService.actions.channelTabsRefresh
              );
              this.isLoggingIn = false;
              this.cdr.detectChanges();
            }, err => {
              this.userIpRestrictionError = err?.status === HTTPResponseStatus.Forbidden && err.error?.errorMessage || null;
              throw err;
            });
          },
          error: (err) => {
            this.handleLoginError(err);
            this.isLoggingIn = false;
            this.cdr.detectChanges();
          }
        });
    }
  }

  forgotAccount() {
    const email = (this.form.get('email').value as string).trim();
    this.router.navigate(['accountforgot', { email }]);
  }

  backToEmailField() {
    this.askPassword = false;
    this.isLoginFailed = false;
    this.userIpRestrictionError = null;
    this.isValidatingEmail = false;
    this.isLoggingIn = false;
    this.form.get('email').enable();
    this.form.get('password').reset('');
    this.cdr.detectChanges();
    this.focusOnEmailField();
  }

  private handleLoginError(err) {
    const httpError: HttpErrorResponse = err && err instanceof HttpErrorResponse ? err : null;
    const error = httpError?.error ? httpError.error.error : null;

    if (error === 'internal_user_login_not_allowed') {
      const email: string = (this.form.get('email').value as string).trim();
      this.router.navigate([MODULE_ROUTES.SSO, { email }]);
    } else if (error === 'portal_user_login_not_allowed') {
      const redirectURL = err.error.redirectURL || '';
      this.router.navigate([redirectURL]);
    } else if (error === 'password_setup_required') {
      const redirectURL = err.error.redirectURL || '';
      this.navigateToUrl(redirectURL);
    }
    else if (error === 'portal_user_login_not_allowed_redirect_provided') {
      const redirectURL = err.error.redirectURL || '';
      if(redirectURL) {
        this.redirectMessage = this.redirectToPortalMessage;
        this.cdr.detectChanges();
        setTimeout(() => this.winRef.openUrl(redirectURL, '_self'), 5000);
      }else{
        if (this.isValidatingEmail) {
          this.form.get('email').enable();
          this.isValidatingEmail = false;
        }
        this.errorMessage = this.loginErrorMessage;
        this.isLoginFailed = true;
        this.userIpRestrictionError = null;
        this.cdr.detectChanges();
      }
    }
    else if (error==='portal_user_login_not_allowed_email_sent'){
      if (this.isValidatingEmail) {
        this.form.get('email').enable();
        this.isValidatingEmail = false;
      }
      this.redirectMessage = this.redirectEmailSentMessage;
      this.cdr.detectChanges();
    }
    else {
      if (this.isValidatingEmail) {
        this.form.get('email').enable();
        this.isValidatingEmail = false;
      }
      this.errorMessage = this.loginErrorMessage;
      this.isLoginFailed = true;
      this.userIpRestrictionError = null;
      this.cdr.detectChanges();
    }
  }

  private navigateToUrl(url: string): void {
    if (!url) {
      return;
    }

    const [route, queryString] = url.split('?');
    
    if (queryString) {
      const queryParams = {};
      queryString.split('&').forEach(param => {
        const [key, value] = param.split('=');
        if (key && value) {
          queryParams[key] = decodeURIComponent(value);
        }
      });
      
      this.router.navigate([route], { queryParams });
    } else {
      this.router.navigate([route]);
    }
  }

  private focusOnEmailField() {
    this.focusOnInputField();
  }

  private focusOnPasswordField() {
    this.focusOnInputField(false);
  }

  private focusOnInputField(emailField = true) {
    const field = emailField ? this.emailInputField : this.passwordInputField;
    const inputElement = field?.nativeElement as HTMLInputElement;
    if (inputElement) {
      inputElement.focus();
    }
  }
}
